import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getUsersByMembershipGroups } from '../Users/UserService';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Select,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Container from 'components/Container';
import { notify } from 'utils/notify';
import { extractErrorMessage } from 'utils/extractErrorMessage';

const GroupUserList = ({ membershipId }) => {
  const [groupedUsers, setGroupedUsers] = useState<Record<string, any[]>>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [availableUsers, setAvailableUsers] = useState<Record<string, any[]>>(
    {},
  );
  const [selectedUsers, setSelectedUsers] = useState<Record<string, any>>({});
  const { membershipid, subscriptionid } = useParams();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    listMembershipUsers();
  }, [membershipId]);

  const listMembershipUsers = () => {
    setLoading(true);
    getUsersByMembershipGroups(membershipid)
      .then((response) => {
        setGroupedUsers(response.data);
        const initialSelections = Object.keys(response.data).reduce(
          (acc, group) => ({ ...acc, [group]: '' }),
          {},
        );
        setSelectedUsers(initialSelections);
      })
      .catch((error) => {
        console.error('Error fetching users by membership:', error);
        notify(extractErrorMessage(error));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleUserSelection = (groupName: string, userId: string) => {
    setSelectedUsers((prev) => ({ ...prev, [groupName]: userId }));
  };

  const handleAddUserToGroup = (groupName: string) => {
    const userId = selectedUsers[groupName];
    if (userId) {
      console.log(`Adding user ${userId} to group ${groupName}`);
      listMembershipUsers();
    }
  };

  const handleInviteUser = (groupName: string) => {
    // Navigate to the "Add Invitation" page, passing in the groupName, membershipid, and subscriptionid
    navigate(
      `/admin/dashboard/membership/${membershipid}/subscription/${subscriptionid}/add-invitation?groupName=${groupName}`,
    );
  };

  return (
    <Container>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom={4}
      >
        <Typography variant="h4">Users on Membership</Typography>
      </Box>
      {loading && <p>Loading...</p>}
      {!loading && Object.keys(groupedUsers).length === 0 && (
        <p>No users found for this membership.</p>
      )}
      {Object.entries(groupedUsers).map(([groupName, users]) => (
        <Box key={groupName} marginBottom={4}>
          <Typography variant="h5" gutterBottom>
            {groupName}
          </Typography>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 750 }} aria-label={`table for ${groupName}`}>
              <TableHead sx={{ bgcolor: 'alternate.main' }}>
                <TableRow>
                  <TableCell>
                    <Typography
                      color={'text.secondary'}
                      variant={'caption'}
                      fontWeight={700}
                      sx={{ textTransform: 'uppercase' }}
                    >
                      ID
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      color={'text.secondary'}
                      variant={'caption'}
                      fontWeight={700}
                      sx={{ textTransform: 'uppercase' }}
                    >
                      First Name
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      color={'text.secondary'}
                      variant={'caption'}
                      fontWeight={700}
                      sx={{ textTransform: 'uppercase' }}
                    >
                      Last Name
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      color={'text.secondary'}
                      variant={'caption'}
                      fontWeight={700}
                      sx={{ textTransform: 'uppercase' }}
                    >
                      Active?
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow
                    key={user.id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>{user.id}</TableCell>
                    <TableCell>{user.firstName}</TableCell>
                    <TableCell>{user.lastName}</TableCell>
                    <TableCell>{user.enabled ? 'Yes' : 'No'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            marginTop={2}
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
          >
            <Select
              value={selectedUsers[groupName] || ''}
              onChange={(e) => handleUserSelection(groupName, e.target.value)}
              displayEmpty
              sx={{ minWidth: 200, marginRight: 2 }}
            >
              <MenuItem value="" disabled>
                Select User
              </MenuItem>
              {availableUsers[groupName]?.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.firstName} {user.lastName}
                </MenuItem>
              ))}
            </Select>
            <Button
              variant="contained"
              onClick={() => handleAddUserToGroup(groupName)}
              disabled={!selectedUsers[groupName]}
            >
              Add User
            </Button>
            {groupName.includes('User') && (
              <Button
                variant="outlined"
                onClick={() => handleInviteUser(groupName)}
                sx={{ marginLeft: 2 }}
              >
                Invite User
              </Button>
            )}
          </Box>
        </Box>
      ))}
    </Container>
  );
};

export default GroupUserList;
